/**
 * GDT Integration Types
 *
 * TypeScript types for the German GDT (Gerätedatentransfer) integration.
 */

// ============================================================
// GDT 3.5 Extended Context Types
// These map to Heidi PatientProfile fields when present
// ============================================================

/**
 * Lab result from GDT 3.5
 * → Maps to: PatientProfile.additional_context
 */
export interface GdtLabResult {
  testName: string;
  result: string;
  unit?: string;
  referenceRange?: string;
  date?: string;
  status?: string;
}

/**
 * Medication from GDT 3.5
 * → Maps to: PatientProfile.current_medications
 */
export interface GdtMedication {
  name: string;
  dosage?: string;
  form?: string;
  instructions?: string;
  pzn?: string; // Pharmazentralnummer (German drug code)
}

/**
 * Vital sign from GDT 3.5
 * → Maps to: PatientProfile.additional_context
 */
export interface GdtVitalSign {
  vitalType: string;
  value: string;
  unit?: string;
  date?: string;
}

/**
 * Allergy from GDT 3.5
 * → Maps to: PatientProfile.allergies
 */
export interface GdtAllergy {
  allergyType?: string;
  substance: string;
  severity?: string;
}

/**
 * Diagnosis from GDT
 * → Maps to: PatientProfile.past_medical_history
 */
export interface GdtDiagnosis {
  icd10Code?: string;
  text?: string;
  certainty?: string;
}

/**
 * Extended context from GDT 3.5 files
 * Contains labs, medications, vitals, allergies, diagnoses that get
 * merged into Heidi's patient profile fields
 */
export interface Gdt35ExtendedContext {
  /** Lab results → additional_context */
  labResults: GdtLabResult[];
  /** Current medications → current_medications */
  medications: GdtMedication[];
  /** Vital signs → additional_context */
  vitalSigns: GdtVitalSign[];
  /** Allergies → allergies */
  allergies: GdtAllergy[];
  /** Diagnoses → past_medical_history */
  diagnoses: GdtDiagnosis[];
  /** Free text comments → additional_context */
  comments: string[];
}

/**
 * Patient demographics received from a GDT file
 */
export interface GdtPatientData {
  /** Allowlisted GDT record type that produced this patient event */
  recordType?: string;
  /** Patient ID from PVS (maps to ehr_patient_id in PatientProfile) */
  patientId: string;
  /** Last name */
  lastName: string;
  /** First name */
  firstName: string;
  /** Date of birth in ISO format (YYYY-MM-DD) - optional, not all PVS include DOB */
  dateOfBirth?: string;
  /** Raw gender value from GDT: 1/M/2/W/D (optional) */
  genderRaw?: string;
  /** Normalized gender for Heidi: male/female/other/unknown (optional) */
  gender?: 'male' | 'female' | 'other' | 'unknown';
  /** GDT-ID of the PVS that sent this data */
  sourceGdtId: string;

  // GDT 3.5 Extended Fields
  /** Extended context from GDT 3.5 (labs, meds, vitals, allergies, diagnoses) */
  extendedContext?: Gdt35ExtendedContext;
  /** Pre-formatted context string for the session (German, human-readable) */
  contextForSession?: string;
}

/**
 * Helper to generate demographic string from patient data
 */
export function getPatientDemographicString(patient: GdtPatientData): string {
  return `${patient.firstName} ${patient.lastName}`;
}

/**
 * Event name for GDT patient received
 * Must match the Rust constant GDT_PATIENT_RECEIVED_EVENT
 */
export const GDT_PATIENT_RECEIVED_EVENT = 'gdt-patient-received';

/** PostHog feature flag key controlling GDT integration availability */
export const GDT_FEATURE_FLAG = 'release-gdt-integration';

/**
 * Paper size options for PDF generation
 */
export type PaperSize = 'A4' | 'Letter';

/**
 * Page margin settings in millimeters
 */
export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Date format presets for different regions/PVS systems
 */
export type DateDisplayFormat = 'german' | 'iso' | 'us' | 'uk';

/**
 * Name display format presets
 */
export type NameDisplayFormat = 'firstLast' | 'lastCommaFirst' | 'lastFirst' | 'custom';

/**
 * Gender display format options
 */
export interface GenderDisplayMapping {
  /** Display text for male (e.g., "männlich", "Male", "M") */
  male: string;
  /** Display text for female (e.g., "weiblich", "Female", "F") */
  female: string;
  /** Display text for other/diverse (e.g., "divers", "Other", "D") */
  other: string;
  /** Display text for unknown (e.g., "unbekannt", "Unknown", "?") */
  unknown: string;
}

/**
 * Patient field mapping configuration for customizing how patient data is
 * displayed and stored per PVS system
 */
export interface PatientFieldMapping {
  /** How to format the patient's display name */
  nameFormat: NameDisplayFormat;

  /**
   * Custom name format template (used when nameFormat is 'custom')
   * Placeholders: {first}, {last}, {title}
   * Example: "{last}, {first}"
   */
  customNameTemplate: string;

  /** Date display format for UI */
  dateFormat: DateDisplayFormat;

  /** Gender display text mapping */
  genderDisplay: GenderDisplayMapping;

  /** Whether to include title (e.g., Dr., Prof.) in display name */
  includeTitle?: boolean;
}

/**
 * Default patient field mapping values
 */
export const DEFAULT_PATIENT_FIELD_MAPPING: PatientFieldMapping = {
  nameFormat: 'firstLast',
  customNameTemplate: '{first} {last}',
  dateFormat: 'german',
  genderDisplay: {
    male: 'männlich',
    female: 'weiblich',
    other: 'divers',
    unknown: 'unbekannt',
  },
};

/**
 * Format an ISO date string (YYYY-MM-DD) to the specified display format
 */
export function formatDateForDisplay(
  isoDate: string | undefined,
  format: DateDisplayFormat
): string {
  if (!isoDate) return '';

  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;

  const [year, month, day] = parts;

  switch (format) {
    case 'german':
      return `${day}.${month}.${year}`;
    case 'iso':
      return isoDate;
    case 'us':
      return `${month}/${day}/${year}`;
    case 'uk':
      return `${day}/${month}/${year}`;
    default:
      return isoDate;
  }
}

/**
 * Format a patient's display name according to the configured format
 */
export function formatPatientDisplayName(
  firstName: string,
  lastName: string,
  mapping: PatientFieldMapping,
  title?: string
): string {
  const titleStr = mapping.includeTitle && title ? title : '';

  switch (mapping.nameFormat) {
    case 'firstLast':
      return titleStr
        ? `${titleStr} ${firstName} ${lastName}`
        : `${firstName} ${lastName}`;
    case 'lastCommaFirst':
      return titleStr
        ? `${titleStr} ${lastName}, ${firstName}`
        : `${lastName}, ${firstName}`;
    case 'lastFirst':
      return titleStr
        ? `${titleStr} ${lastName} ${firstName}`
        : `${lastName} ${firstName}`;
    case 'custom':
      return mapping.customNameTemplate
        .replace('{first}', firstName)
        .replace('{last}', lastName)
        .replace('{title}', titleStr)
        .trim();
    default:
      return `${firstName} ${lastName}`;
  }
}

/**
 * Get display text for a gender value using the mapping
 */
export function formatGenderForDisplay(
  gender: GdtPatientData['gender'],
  mapping: GenderDisplayMapping
): string {
  switch (gender) {
    case 'male':
      return mapping.male;
    case 'female':
      return mapping.female;
    case 'other':
      return mapping.other;
    case 'unknown':
      return mapping.unknown;
    default:
      return '';
  }
}

/**
 * PDF export settings for GDT integration
 */
export interface GdtPdfSettings {
  /** Whether PDF attachment is enabled */
  enabled: boolean;

  /** Folder where PDFs are exported */
  exportFolder: string;

  // Header settings
  /** Whether to include a header in the PDF */
  headerEnabled: boolean;

  /** HTML content for the header (supports basic formatting) */
  headerHtml: string;

  /** Path to logo image file (optional) */
  headerLogoPath?: string;

  /** Maximum height for the logo in millimeters */
  headerLogoMaxHeightMm: number;

  /** Padding around the logo in millimeters */
  headerLogoPaddingMm: number;

  // Footer settings
  /** Whether to include a footer in the PDF */
  footerEnabled: boolean;

  /** HTML content for the footer (supports basic formatting) */
  footerHtml: string;

  // Page settings
  /** Paper size (A4 or Letter) */
  paperSize: PaperSize;

  /** Page margins in millimeters */
  margins: PageMargins;

  /**
   * Network path the PVS resolves PDF attachments from, when it reads them
   * from its own server rather than Heidi's local disk. When set, GDT field
   * 6305 is written as `{networkPath}\{filename}` instead of a path relative
   * to the export/output folder.
   */
  networkPath: string;

  /**
   * Template for the PDF attachment filename. Supports placeholder tokens:
   * PatId (patient ID), PatName (last name), PatVorname (first name), PatGeb
   * (date of birth), DocumentTitle (document title). When empty, the
   * filename defaults to `{patient_name}_{timestamp}.pdf`.
   */
  filenameTemplate: string;
}

/**
 * Patient/document fields available to a PDF filename template.
 *
 * Mirrors `PdfFilenameTokens` on the Rust side.
 */
export interface PdfFilenameTokens {
  patientId?: string;
  lastName?: string;
  firstName?: string;
  /** Date of birth in ISO format (YYYY-MM-DD) */
  dateOfBirth?: string;
  /** Document title — the same value written to GDT field 6304 */
  documentTitle?: string;
}

/**
 * Well-known GDT 6330 "Befundkennzeichnung" (findings category) codes.
 *
 * PVS vendors may use custom codes, and Medical Office uses custom open-category
 * names/external abbreviations, so `string` is also accepted.
 */
export type GdtCategoryCode =
  | 'AN' // Anamnese (history / subjective)
  | 'BE' // Befund (findings / objective)
  | 'DI' // Diagnose (diagnosis)
  | 'PR' // Prozedur (procedure / plan)
  | 'TH' // Therapie (therapy)
  | (string & {}); // Allow custom PVS-specific codes

/**
 * A mapping entry from note section heading to GDT category field and code.
 *
 * In standard GDT mode, `fieldId` is the category-code field written before
 * each section and the section content is written to the next field number.
 * Existing integrations default to 6330 + 6331. Medical Office uses the same
 * field pair, with the category value set to the configured custom text category
 * external abbreviation.
 */
export interface SectionCategoryEntry {
  /** Note section heading (matched case-insensitively) */
  sectionName: string;
  /**
   * GDT findings category code (e.g., "AN", "BE", "PR") or a PVS-specific
   * open-category name such as a Medical Office external abbreviation.
   */
  categoryCode: GdtCategoryCode;
  /**
   * GDT field identifier (e.g. `6330`, `6332`, `6334`).
   *
   * In standard mode this is the category field and content is emitted under
   * the following field. In direct field-id mode this is the content field
   * routed by the PVS into its configured text group.
   */
  fieldId?: string;
}

/**
 * A structured note section with heading and content
 *
 * When sent to the backend, each section gets its own 6330 category code
 * based on the section_category_mapping in config.
 */
export interface NoteSection {
  /** Section heading (e.g., "Subjective", "Assessment", "Plan") */
  heading: string;
  /** Section content (can be multi-line) */
  content: string;
  /** Per-note override of 6330 category code (takes precedence over config mapping) */
  categoryCode?: string;
}

/**
 * Default 6330 category code when no mapping matches
 */
export const DEFAULT_CATEGORY_CODE = 'BE';

/**
 * GDT configuration settings
 */
export interface GdtConfig {
  /** Whether GDT integration is enabled */
  enabled: boolean;

  /** Folder where the PVS writes GDT files for Heidi to read (incoming) */
  inputFolder: string;

  /** Folder where Heidi writes GDT files for the PVS to read (outgoing) */
  outputFolder: string;

  /** Heidi's GDT-ID (default: HEIDI01) */
  heidiGdtId: string;

  /** PVS system's GDT-ID (e.g., ALBIS01, MEDOF01) */
  pvsGdtId: string;

  /** Human-readable PVS system name (e.g., "CGM ALBIS") */
  pvsSystemName: string;

  /** GDT version to use (default: 02.10) */
  gdtVersion: string;

  /**
   * Fixed input filename to watch for incoming GDT files (optional)
   *
   * When set, the watcher only processes files matching this filename
   * (case-insensitive). PVS systems typically write to a fixed filename
   *
   * If not set or empty, all `.gdt` files in the folder are processed.
   */
  inputFilename?: string;

  /**
   * Fixed output filename for GDT files (optional)
   *
   * Some PVS systems (e.g., x.isynet) expect a fixed filename instead of
   * timestamped filenames. When set, this filename will be used for all
   * GDT output files, overwriting any existing file with the same name.
   *
   * If not set or empty, a unique timestamped filename is generated
   * for each note (e.g., HEIDI_20250202143000_001.gdt).
   *
   * @example "heidi_note.gdt"
   */
  outputFilename?: string;

  /** Whether to delete incoming GDT files after processing (GDT convention, default: true) */
  deleteAfterRead?: boolean;

  /** PDF export settings (header, footer, logo) */
  pdfSettings: GdtPdfSettings;

  /** Patient field mapping and display settings */
  patientMapping: PatientFieldMapping;

  /**
   * Whether sectional notes are enabled.
   *
   * When true, JSON template mode is auto-enabled for GDT sessions
   * and per-section 6330 category values are used when pushing notes.
   */
  enableSectionalNotes?: boolean;

  /**
   * Section-to-GDT-field/category mapping for structured note export.
   *
   * Maps note section headings to a GDT category field identifier and category
   * value. Matching is case-insensitive.
   */
  sectionCategoryMapping?: SectionCategoryEntry[];

  /**
   * Default 6330 category value for sections not in the mapping
   * Also used as blanket category when flat note content is sent.
   * Defaults to "BE" (Befund/findings).
   */
  defaultCategoryCode?: GdtCategoryCode;

  /**
   * Whether to emit the section heading as the first 6331 content line.
   * Defaults to false — the 6330 category code is the label for standard PVS systems.
   * Set to true only for PVS systems confirmed to require the heading as 6331 text.
   */
  includeHeadingInSections?: boolean;

  /**
   * Whether each section's content is written directly under the configured
   * `fieldId` instead of as a category field + following content field pair.
   *
   * Duria/Duria2 enable this by default because they import GDT text by
   * `Feldkennung,Textgruppe`, keying on the field number rather than a letter
   * code. Other PVS systems can also enable it when their GDT importer is
   * customized to route by field number. Defaults to false for standard setups.
   */
  sectionsAsFieldIds?: boolean;

  /**
   * Output character encoding for GDT files.
   * Defaults to 'windows1252' (GDT 2.1 standard, maximum PVS compatibility).
   * Only change to 'utf8' when the PVS vendor explicitly confirms UTF-8 is required.
   */
  encoding?: 'windows1252' | 'utf8';

  /**
   * Whether the file watcher should auto-start on app launch.
   * Set to false when the user explicitly stops the watcher via the UI.
   * Defaults to true.
   */
  watcherAutoStart: boolean;
}

/**
 * GDT file watcher status information
 */
export interface WatcherStatusInfo {
  /** Whether the watcher is currently running */
  running: boolean;
  /** Input folder being monitored */
  inputFolder: string;
  /** Number of files processed since watcher started */
  filesProcessed: number;
  /** Last error message, if any */
  lastError?: string;
}

/**
 * Result of logo validation
 */
export interface LogoValidation {
  valid: boolean;
  width?: number;
  height?: number;
  format?: string;
  error?: string;
}

/**
 * Result of PDF generation
 */
export interface PdfGenerationResult {
  success: boolean;
  filePath?: string;
  error?: string;
}
